import React from "react";
import { connect, useDispatch } from "react-redux";
import { useTheme } from "styled-components";
import useIsMobile from "../../../../hooks/deviceChecker";
import { useLayout } from "../../../../provider/LayoutProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../../../components/Button";
import VideoPlayer from "../../../../components/VideoPlayer";
import PdfViewer from "../../../../components/PdfPlayer";
import Quiz from "./components/Quiz";
import Units from "../../../../components/Units";
import {
	Content,
	Title,
	Subtitle,
	Module,
	NavUnits,
	NavUnitsBtn,
	TimerWrapper,
	TimerText,
} from "./styles";
import Typography from "../../../../components/Typography";
import Icon from "../../../../components/Icon";
import { Flex } from "../../../../components/StyledComponents";
import ArrowRight from "../../../../assets/icons/icon-cool/outline/Arrows-chevron/ArrowRight.svg";
import ArrowLeft from "../../../../assets/icons/icon-cool/outline/Arrows-chevron/ArrowLeft.svg";
import Sidebar from "../../../../components/Sidebar";
import { useParams, useLocation } from "react-router-dom";
import { getOneCourse } from "../../../../store/actions/course";
import {
	CourseInDto,
	LabComponent,
	LabUnit,
} from "../../../../types/course.dto";
import {
	resetCreateNewEnrollment,
	updateOneEnrollment,
} from "../../../../store/actions/enrollment";
import { getComponentFile } from "../../../../store/actions/file";
import { resetNewAnswerQuiz } from "../../../../store/actions/quiz";
import { Action, Dispatch } from "redux";

interface ComponentProps {
	course: CourseInDto;
	courseStates: {
		loading: boolean;
	};
	newAnswersQuiz;
	newAnswersQuizStates;
}

const Component = ({ course, courseStates }: ComponentProps) => {
	const theme = useTheme() as { [key: string]: { [key: string]: string } };
	const isMobile = useIsMobile();
	const params = useParams();
	const location = useLocation();
	const dispatch = useDispatch<Dispatch<Action>>();
	const [currentCourseId, setCurrentCourseId] = useState<number>();
	const [currentComponentId, setCurrentComponent] = useState<number>();
	const [currentUnitId, setCurrentUnit] = useState<number>();
	const [visible, setVisible] = useState<boolean>(false);
	const { layout, setLayout, setIsPageLoading } = useLayout();
	const [timeLeft, setTimeLeft] = useState(30);
	const [isTimeUp, setIsTimeUp] = useState(false);

	useEffect(() => {
		setTimeLeft(30);
		setIsTimeUp(false);
	}, [currentComponentId, currentUnitId, currentCourseId]);

	useEffect(() => {
		if (timeLeft <= 0) {
			setIsTimeUp(true);
			return;
		}

		const timer = setTimeout(() => {
			setTimeLeft((prev) => prev - 1);
		}, 1000);

		return () => clearTimeout(timer);
	}, [timeLeft]);

	/*
	 * Check if is the last unit component
	 */
	const isLastUnitComponent = useMemo(() => {
		if (course?.id === currentCourseId && currentUnitId) {
			const currentUnit = course?.labUnit?.find(
				(unit) => unit?.id === currentUnitId
			);

			// Ordenar labComponent por sort_order
			const sortedLabComponents = currentUnit?.labComponent
				?.slice()
				.sort((a, b) => a.sort_order - b.sort_order);

			const lastComponentId =
				sortedLabComponents?.[sortedLabComponents.length - 1]?.id;

			return currentComponentId === lastComponentId;
		}

		return false;
	}, [course, currentUnitId, currentComponentId]);

	/*
	 * Check if is the last course unit component
	 */
	const isLastCourseUnitComponent = useMemo(() => {
		if (course?.id === currentCourseId && currentUnitId && currentComponentId) {
			// Ordenar labUnit por sort_order
			const sortedLabUnits = course?.labUnit
				?.slice()
				.sort((a, b) => a.sort_order - b.sort_order);

			const lastUnit = sortedLabUnits?.[sortedLabUnits.length - 1];

			// Ordenar labComponent del lastUnit por sort_order
			const sortedLabComponents = lastUnit?.labComponent
				?.slice()
				.sort((a, b) => a.sort_order - b.sort_order);

			const lastComponentId =
				sortedLabComponents?.[sortedLabComponents.length - 1]?.id;

			return (
				currentUnitId === lastUnit?.id && currentComponentId === lastComponentId
			);
		}

		return false;
	}, [course, currentUnitId, currentComponentId]);

	/*
	 * Check if it is the first unit component
	 */
	const isFirstUnitComponent = useMemo(() => {
		if (course?.id === currentCourseId && currentUnitId) {
			const currentUnit = course?.labUnit?.find(
				(unit) => unit?.id === currentUnitId
			);

			// Ordenar labComponent por sort_order
			const sortedLabComponents = currentUnit?.labComponent
				?.slice()
				.sort((a, b) => a.sort_order - b.sort_order);

			const firstComponentId = sortedLabComponents?.[0]?.id;

			return currentComponentId === firstComponentId;
		}

		return false;
	}, [course, currentUnitId, currentComponentId]);

	/*
	 * Check if it is the first course unit component
	 */
	const isFirstCourseUnitComponent = useMemo(() => {
		if (course?.id === currentCourseId && currentUnitId && currentComponentId) {
			// Ordenar labUnit por sort_order
			const sortedLabUnits = course?.labUnit
				?.slice()
				.sort((a, b) => a.sort_order - b.sort_order);

			const firstUnit = sortedLabUnits?.[0];

			// Ordenar labComponent del firstUnit por sort_order
			const sortedLabComponents = firstUnit?.labComponent
				?.slice()
				.sort((a, b) => a.sort_order - b.sort_order);

			const firstComponentId = sortedLabComponents?.[0]?.id;

			return (
				currentUnitId === firstUnit?.id &&
				currentComponentId === firstComponentId
			);
		}

		return false;
	}, [course, currentUnitId, currentComponentId]);

	const currentComponentIndex = useMemo((): number | undefined => {
		if (course?.id === currentCourseId && currentUnitId) {
			const unit = course?.labUnit?.find((unit) => unit?.id === currentUnitId);
			const sortedComponents = unit?.labComponent?.sort(
				(a, b) => a.sort_order - b.sort_order
			);
			const currentIndex = sortedComponents?.findIndex(
				(component) => component?.id === currentComponentId
			);

			return currentIndex !== undefined ? currentIndex + 1 : undefined;
		}

		return undefined;
	}, [course, currentUnitId]);

	const currentUnitIndex = useMemo((): number | undefined => {
		if (course?.id === currentCourseId && currentUnitId) {
			const sortedUnits = course?.labUnit?.sort(
				(a, b) => a.sort_order - b.sort_order
			);
			const currentUnitIndex = sortedUnits?.findIndex(
				(unit) => unit?.id === currentUnitId
			);

			return currentUnitIndex;
		}

		return undefined;
	}, [course, currentUnitId, currentCourseId]);

	const currentComponent = useMemo((): LabComponent | undefined => {
		if (course?.id === currentCourseId && currentUnitId && currentComponentId) {
			const unit = course?.labUnit?.find((unit) => unit?.id === currentUnitId);
			const component = unit?.labComponent?.find(
				(component) => component?.id === currentComponentId
			);
			return component;
		}

		return undefined;
	}, [course, currentUnitId, currentComponentId]);

	const currentUnit = useMemo((): LabUnit | undefined => {
		if (course?.id === currentCourseId && currentUnitId) {
			return course?.labUnit?.find((unit) => unit?.id === currentUnitId);
		}

		return undefined;
	}, [course, currentUnitId]);

	const nextUnit = useMemo((): LabUnit | undefined => {
		if (course?.id === currentCourseId && currentUnitId) {
			const sortedUnits = course?.labUnit
				?.slice()
				.sort((a, b) => a.sort_order - b.sort_order);
			const currentUnitIndex = sortedUnits?.findIndex(
				(unit) => unit?.id === currentUnitId
			);
			const unit = sortedUnits?.[currentUnitIndex + 1];

			if (unit && unit?.labComponent) {
				unit.labComponent = unit.labComponent
					.slice()
					.sort((a, b) => a.sort_order - b.sort_order);
			}

			return unit;
		}

		return undefined;
	}, [course, currentUnitId]);

	const prevUnit = useMemo((): LabUnit | undefined => {
		if (course?.id === currentCourseId && currentUnitId) {
			const sortedUnits = course?.labUnit
				?.slice()
				.sort((a, b) => a.sort_order - b.sort_order);
			const currentUnitIndex = sortedUnits?.findIndex(
				(unit) => unit?.id === currentUnitId
			);
			const unit = sortedUnits?.[currentUnitIndex - 1];

			if (unit && unit?.labComponent) {
				unit.labComponent = unit.labComponent
					.slice()
					.sort((a, b) => a.sort_order - b.sort_order);
			}

			return unit;
		}

		return undefined;
	}, [course, currentCourseId, currentUnitId]);

	const nextComponent = useMemo((): LabComponent | undefined => {
		if (course?.id === currentCourseId && currentUnitId && currentComponentId) {
			const sortedUnits = course?.labUnit
				?.slice()
				.sort((a, b) => a.sort_order - b.sort_order);
			const unit = sortedUnits?.find((item) => item?.id === currentUnitId);

			const sortedComponents = unit?.labComponent
				?.slice()
				.sort((a, b) => a.sort_order - b.sort_order) as LabComponent[];

			const currentComponentIndex = sortedComponents?.findIndex(
				(component) => component?.id === currentComponentId
			) as number;

			const nextComponentIndex = currentComponentIndex + 1;

			const component =
				sortedComponents[nextComponentIndex] ||
				nextUnit?.labComponent
					?.slice()
					.sort((a, b) => a.sort_order - b.sort_order)?.[0];

			return component;
		}

		return undefined;
	}, [course, nextUnit, currentComponentId, currentUnitId]);

	const prevComponent = useMemo((): LabComponent | undefined => {
		if (course?.id === currentCourseId && currentUnitId && currentComponentId) {
			const sortedUnits = course?.labUnit
				?.slice()
				.sort((a, b) => a.sort_order - b.sort_order);
			const unit = sortedUnits?.find((item) => item?.id === currentUnitId);

			const sortedComponents = unit?.labComponent
				?.slice()
				.sort((a, b) => a.sort_order - b.sort_order) as LabComponent[];

			const currentComponentIndex = sortedComponents?.findIndex(
				(component) => component?.id === currentComponentId
			) as number;

			const prevComponentIndex = currentComponentIndex - 1;

			const component =
				sortedComponents[prevComponentIndex] ||
				prevUnit?.labComponent
					?.slice()
					.sort((a, b) => a.sort_order - b.sort_order)?.[0];

			return component;
		}

		return undefined;
	}, [course, prevUnit, currentComponentId, currentUnitId]);

	const countComponentsUntilCurrent = (sortedLabUnits, unitId, componentId) => {
		let totalComponents = 0;

		// Find the index of the current unit
		const currentUnitIndex = sortedLabUnits.findIndex(
			(unit) => unit.id === unitId
		);
		if (currentUnitIndex === -1) return 0; // If current unit not found, return 0

		// Iterate over previous units
		for (let i = 0; i < currentUnitIndex; i++) {
			totalComponents += sortedLabUnits[i].labComponent.length;
		}

		// Find the index of the current component within its unit
		const currentUnit = sortedLabUnits[currentUnitIndex];
		const currentComponentIndex = currentUnit.labComponent.findIndex(
			(component) => component.id === componentId
		);
		if (currentComponentIndex === -1) return 0; // If current component not found, return 0

		// Add the index of current component within its unit
		totalComponents += currentComponentIndex;

		return totalComponents;
	};

	const updateProgress = useCallback(() => {
		const enrollment = course.labEnrollment[0];

		if (enrollment) {
			const sorted_units = course?.labUnit?.sort(
				(a, b) => a.sort_order - b.sort_order
			);
			const last_unit = sorted_units?.find(
				(unit) => unit?.id === enrollment?.last_unit_id
			);
			const last_unit_sorted_components = last_unit?.labComponent?.sort(
				(a, b) => a.sort_order - b.sort_order
			);
			const last_component = last_unit_sorted_components?.find(
				(component) => component?.id === enrollment?.last_component_id
			);
			const countComponents = countComponentsUntilCurrent(
				sorted_units,
				currentUnitId,
				currentComponentId
			);

			if (isLastCourseUnitComponent && currentComponent?.type !== "Quiz") {
				// En esta condicion verificamos si nos encontramos en el ultimo componente
				// de la ultima unidad y si este componente NO es un quiz
				// En caso que se den ambas condiciones actualizamos el enrollment finalizando el curso
				//
				// En caso que el ultimo componente de la ultima unidad SI es un quiz, al finalizar el quiz podremos
				// finalizar el curso.

				dispatch(
					updateOneEnrollment({
						idEnrollment: enrollment?.id,
						body: {
							last_unit_id: currentUnitId,
							last_component_id: currentComponentId,
							components_completed: course.total_components,
							state: "Finished",
							completed_date: new Date(),
						},
					})
				);
			} else if (last_unit && currentUnit) {
				// Necesitaos verificar que la ultima unidad registrada en la base de datos
				// sea menor que la unidad en la cual nos encontramos parados
				// De ser asi entonces debemos actualizar el progreso

				if (currentUnit?.sort_order > last_unit?.sort_order) {
					dispatch(
						updateOneEnrollment({
							idEnrollment: enrollment?.id,
							body: {
								last_unit_id: currentUnitId,
								last_component_id: currentComponentId,
								components_completed: countComponents,
							},
						})
					);

					// En caso que la condicion anterior no se valide
					// Debemos entonces ver si es la misma unidad
					// De ser asi entonces verificamos si el ultimo componente registrado en la base de datos
					// sea menor al componente en el que nos encontramos padados
					// De ser asi entonces debemos actualizar el progreso
				} else if (currentUnit?.sort_order === last_unit?.sort_order) {
					if (currentComponent && last_component) {
						if (currentComponent?.sort_order > last_component?.sort_order) {
							dispatch(
								updateOneEnrollment({
									idEnrollment: enrollment?.id,
									body: {
										last_unit_id: currentUnitId,
										last_component_id: currentComponentId,
										components_completed: countComponents,
									},
								})
							);
						}
					}
				}
			} else if (isFirstCourseUnitComponent && !last_unit && !last_component) {
				dispatch(
					updateOneEnrollment({
						idEnrollment: enrollment?.id,
						body: {
							last_unit_id: currentUnitId,
							last_component_id: currentComponentId,
							components_completed: countComponents,
						},
					})
				);
			}
		}
	}, [
		course,
		currentUnitId,
		currentComponent,
		currentComponentId,
		isFirstCourseUnitComponent,
		isLastCourseUnitComponent,
	]);

	const finishedCourse = (): void => {
		if (currentUnit && currentComponent) {
			const enrollment = course.labEnrollment[0];

			dispatch(
				updateOneEnrollment({
					idEnrollment: enrollment?.id,
					body: {
						last_unit_id: currentUnit?.id,
						last_component_id: currentComponent?.id,
						components_completed: course.total_components,
						state: "Finished",
						completed_date: new Date(),
					},
				})
			);
		}
	};

	useEffect(() => {
		if (isFirstCourseUnitComponent) {
			updateProgress();
		}
	}, [isFirstCourseUnitComponent]);

	useEffect(() => {
		setLayout({
			...layout,
			showSubHeader: false,
			showFilters: false,
			showSearchBar: false,
		});
	}, [isMobile, params, location]);

	useEffect(() => {
		if (params) {
			params?.idCourse && setCurrentCourseId(Number(params?.idCourse));
			params?.idUnit && setCurrentUnit(Number(params?.idUnit));
			params?.idComponent && setCurrentComponent(Number(params?.idComponent));
		}
	}, [params]);

	useEffect(() => {
		if (currentCourseId) {
			dispatch(getOneCourse({ idCourse: currentCourseId }));
		}
	}, [currentCourseId]);

	useEffect(() => {
		if (!course || courseStates.loading) {
			setIsPageLoading(true);
		} else {
			setIsPageLoading(false);
		}
	}, [course, courseStates]);

	useEffect(() => {
		if (currentUnit && currentComponent) {
			dispatch(resetNewAnswerQuiz());
			updateProgress();
		}
	}, [course, currentUnit, currentComponent]);

	useEffect(() => {
		if (currentComponent) {
			if (
				currentComponent.Files &&
				currentComponent.Files.length > 0 &&
				!currentComponent.Files[0].Blob
			) {
				const idFile = currentComponent.Files[0].id;
				dispatch(
					getComponentFile({ idFile, idComponent: currentComponent.id })
				);
			}
		}
	}, [currentComponent]);

	useEffect(() => {
		dispatch(resetNewAnswerQuiz());
		dispatch(resetCreateNewEnrollment());
	}, []);

	return (
		<>
			{currentComponent?.type === "Video" && (
				<VideoPlayer
					videoSource={currentComponent?.content_url}
					title={
						<>
							<b>
								<Typography as="p" size="xs">
									{currentUnit?.title}
								</Typography>
							</b>
							<Typography as="p" size="xs">
								{currentComponent?.title}
							</Typography>
						</>
					}
				/>
			)}

			{currentComponent?.type === "PDF" && (
				<PdfViewer
					fileUrl={currentComponent?.Files[0]?.Blob}
					fileName={currentComponent?.title}
					blockDownloadFiles={true}
					key={"pdf"}
				/>
			)}

			<Content>
				{(currentComponent?.type === "Video" ||
					currentComponent?.type === "PDF") && (
					<>
						<Flex align="space">
							<Flex align="left" column>
								<Title>{currentUnit?.title}</Title>
								<div style={{ height: 4 }} />

								<Subtitle>{currentComponent?.title}</Subtitle>
								<div style={{ height: 4 }} />

								<div>{currentComponent?.description}</div>
								<div style={{ height: 4 }} />

								<div>
									Unidad {(currentUnitIndex || 0) + 1}/{course?.labUnit?.length}
								</div>
							</Flex>

							<Button
								options={{
									type: "outline",
									skin: "secondary",
									size: "md",
								}}
								style={{ justifyContent: "space-between" }}
								onClick={() => setVisible(true)}
							>
								<Flex align="center" justify="center" gap={5}>
									<Typography as="p" size="xs">
										Unidades
									</Typography>
								</Flex>
							</Button>
						</Flex>
						{!isTimeUp && (
							<TimerWrapper>
								<TimerText>
									El siguiente contenido se habilita en: {timeLeft}{" "}
									{timeLeft === 1 ? "segundo" : "segundos"}
								</TimerText>
							</TimerWrapper>
						)}
						{!isLastCourseUnitComponent && (
							<>
								<div style={{ height: 20 }} />
								<NavUnits
									disabled={!isTimeUp}
									to={`/dashboard/mis-cursos/${currentCourseId}/${
										isLastUnitComponent ? nextUnit?.id : currentUnit?.id
									}/${
										isLastUnitComponent
											? nextUnit?.labComponent[0].id
											: nextComponent?.id
									}`}
								>
									<Module>
										{isLastUnitComponent
											? nextUnit?.labComponent[0]?.title
											: nextComponent?.title}
										<Icon
											icon={ArrowRight}
											size="20px"
											color={theme.colors.Primary}
										/>
									</Module>
								</NavUnits>
							</>
						)}

						{!isFirstCourseUnitComponent && (
							<>
								<div style={{ height: 20 }} />

								<NavUnits
									to={`/dashboard/mis-cursos/${currentCourseId}/${
										isFirstUnitComponent ? prevUnit?.id : currentUnit?.id
									}/${
										isFirstUnitComponent
											? prevUnit?.labComponent[
													prevUnit?.labComponent.length - 1
											  ].id
											: prevComponent?.id
									}`}
								>
									<Module>
										{isFirstUnitComponent
											? prevUnit?.labComponent[
													prevUnit?.labComponent?.length - 1
											  ]?.title
											: prevComponent?.title}
										<Icon
											icon={ArrowLeft}
											size="20px"
											color={theme.colors.Primary}
										/>
									</Module>
								</NavUnits>
							</>
						)}

						{isLastCourseUnitComponent && (
							<>
								<div style={{ height: 20 }} />
								<NavUnitsBtn
									disabled={!isTimeUp}
									options={{ skin: "white", size: "md", type: "filled" }}
									onClick={() => {
										finishedCourse();
										window.location.href = `/dashboard/mis-cursos/${course?.id}/valoration`;
									}}
								>
									<Typography as="h3" size="sm">
										Continuar
									</Typography>
									<div style={{ height: 7 }} />
									<Module style={{ justifyContent: "flex-end", width: "100%" }}>
										<Icon
											icon={ArrowRight}
											size="20px"
											color={theme.colors.Primary}
										/>
									</Module>
								</NavUnitsBtn>
							</>
						)}
					</>
				)}

				{currentComponent?.type === "Quiz" && (
					<>
						<Quiz
							idQuiz={currentComponent?.labQuiz?.id}
							idEnrollment={course?.labEnrollment[0]?.id}
							isLastCourseUnitComponent={isLastCourseUnitComponent}
							enrollment={course?.labEnrollment[0]}
							currentUnit={currentUnit}
							currentComponent={currentComponent}
							nextComponentUrl={`/dashboard/mis-cursos/${currentCourseId}/${
								isLastUnitComponent ? nextUnit?.id : currentUnit?.id
							}/${
								isLastUnitComponent
									? nextUnit?.labComponent[0].id
									: nextComponent?.id
							}`}
							previousComponentUrl={`/dashboard/mis-cursos/${currentCourseId}/${
								isFirstUnitComponent ? prevUnit?.id : currentUnit?.id
							}/${
								isFirstUnitComponent
									? prevUnit?.labComponent[prevUnit?.labComponent.length - 1].id
									: prevComponent?.id
							}`}
						/>
					</>
				)}
			</Content>

			<Sidebar
				position="bottom"
				visible={visible}
				closeType="back"
				onClose={() => {
					setVisible(false);
				}}
				width="100vw"
				// height="calc(100vh - 214px)"
				title="Unidades"
				styles={{
					height: "calc(100% - 150px)",
					top: "150px",
				}}
			>
				<Units
					course={course}
					currentUnit={currentUnit}
					currentComponent={currentComponent}
					linkComponents
					onClick={() => setVisible(false)}
				/>
			</Sidebar>
		</>
	);
};

const state = ({ courseStore, quizStore }) => {
	const { data: course, states: courseStates } = courseStore.getOne;
	const { data: newAnswersQuiz, states: newAnswersQuizStates } =
		quizStore.newAnswersQuiz;

	return {
		course,
		courseStates,
		newAnswersQuiz,
		newAnswersQuizStates,
	};
};

export default connect(state)(Component);
